import React, { useEffect, useState } from 'react';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  Alert,
  AlertDescription,
  Button,
} from '@roger/ui-components';
import { AlertCircle, Loader2, RefreshCw } from 'lucide-react';
import { getReferralDocPresignedUrlIntakeIntakeIdReferralDocGet } from '../services/api/sdk.gen';
import type { IntakeRecord } from '../store/slices/intakeSlice';

interface FailedIntakeDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  intake: IntakeRecord | null;
  onRetry: () => void;
}

const FailedIntakeDrawer: React.FC<FailedIntakeDrawerProps> = ({
  open,
  onOpenChange,
  intake,
  onRetry,
}) => {
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [pdfLoading, setPdfLoading] = useState(false);

  // Load PDF URL when drawer opens with a new intake
  useEffect(() => {
    if (!open || !intake) {
      setPdfUrl(null);
      return;
    }

    const loadPdfUrl = async () => {
      setPdfLoading(true);
      try {
        const response =
          await getReferralDocPresignedUrlIntakeIntakeIdReferralDocGet({
            path: { intake_id: intake.id },
          });
        if (response.data?.url) {
          setPdfUrl(response.data.url);
        }
      } catch (error) {
        console.error('Failed to load PDF URL:', error);
      } finally {
        setPdfLoading(false);
      }
    };

    loadPdfUrl();
  }, [open, intake]);

  const handleClose = () => {
    onOpenChange(false);
  };

  return (
    <Sheet open={open} onOpenChange={handleClose}>
      <SheetContent
        side='right'
        className='w-[600px] sm:w-[800px] sm:max-w-none flex flex-col'
      >
        <SheetHeader>
          <SheetTitle className='flex items-center gap-2'>
            <AlertCircle className='h-5 w-5 text-red-600' />
            Failed Intake
          </SheetTitle>
        </SheetHeader>

        <div className='h-full flex flex-col flex-1 min-h-0'>
          {/* Failure Reason Alert */}
          {intake?.processing_errors && (
            <Alert className='border-red-200 bg-red-50 mb-4'>
              <AlertDescription className='text-red-800 flex items-center justify-between'>
                <div className='flex items-center gap-2'>
                  <AlertCircle className='h-4 w-4 text-red-600' />
                  <span>{intake.processing_errors}</span>
                </div>
                <Button
                  onClick={onRetry}
                  size='sm'
                  className='bg-red-600 hover:bg-red-700 ml-4'
                >
                  <RefreshCw className='w-4 h-4 mr-2' />
                  Retry
                </Button>
              </AlertDescription>
            </Alert>
          )}

          {/* PDF Viewer */}
          <div className='flex-1 overflow-hidden'>
            {pdfLoading ? (
              <div className='h-full flex items-center justify-center text-muted-foreground'>
                <Loader2 className='h-6 w-6 animate-spin mr-2' />
                Loading document...
              </div>
            ) : pdfUrl ? (
              <iframe
                src={`${pdfUrl}#navpanes=0`}
                className='w-full h-full border-0 rounded-lg'
                title='Intake Document'
              />
            ) : (
              <div className='h-full flex items-center justify-center text-muted-foreground'>
                No document available
              </div>
            )}
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default FailedIntakeDrawer;
